/**
 * Instagram adapter for social stats data
 */

import type { InstagramCreator, InstagramCreatorDailyEntry, Maybe } from '@frontend/gql/social/graphql';
import type { SocialStatsDataAdapter, SocialStatsDataPoint, SocialStatsTab } from '@/types/social-stats';

/**
 * Transform Instagram daily entry to generic social stats data point
 */
const transformInstagramDailyEntry = (entry: InstagramCreatorDailyEntry): SocialStatsDataPoint => ({
  date: entry.date || '',
  value: entry.value || 0,
  // Include any additional Instagram-specific properties
  ...entry,
});

/**
 * Instagram social stats adapter
 */
export class InstagramSocialStatsAdapter implements SocialStatsDataAdapter<Maybe<InstagramCreator>> {
  getTabs(): SocialStatsTab[] {
    return [
      { key: 'engagements', heading: 'Engagements', info: 'View Details', lineColor: '#6C77DE' },
      { key: 'reach', heading: 'Reach', info: 'View Details', lineColor: '#6C77DE' },
    ];
  }

  getDefaultTab(): string {
    return 'engagements';
  }

  getEngagementsData(source: Maybe<InstagramCreator>): SocialStatsDataPoint[] {
    const rawData = source?.insights?.daily?.engagement || [];
    return rawData.map(transformInstagramDailyEntry);
  }

  getReachData(source: Maybe<InstagramCreator>): SocialStatsDataPoint[] {
    const rawData = source?.insights?.daily?.reach || [];
    return rawData.map(transformInstagramDailyEntry);
  }

  // Optional: Support for additional tabs if needed in the future
  getDataForTab(source: Maybe<InstagramCreator>, tabKey: string): SocialStatsDataPoint[] {
    switch (tabKey) {
      case 'engagements':
        return this.getEngagementsData(source);
      case 'reach':
        return this.getReachData(source);
      default:
        // For future extensibility, could add support for other Instagram metrics
        return [];
    }
  }

  getSectionTitle(): string {
    return 'Instagram Stats';
  }

  getSectionIconBgColor(): string {
    return '#6C77DE';
  }
}

/**
 * Default Instagram social stats adapter instance
 */
export const instagramSocialStatsAdapter = new InstagramSocialStatsAdapter();
